import React, { useEffect, useRef, useState } from "react";
import { AppDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { fetchCustomers, deleteCustomer } from "../../features/customer/CustomerServices";
import { FaChevronLeft, FaChevronRight, FaSearch } from "react-icons/fa";
import { deleteIcon, editIcon } from "../../asset";
import { useDebounce } from "../../util/hooks/useDebounce";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ECSLoader from "../../components/common/EcsLoader";
import Pagination from "../../components/common/Pagination";

export default function CustomerList() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const [pageSize, setPageSize] = useState(50);

    const isSelectMode = searchParams.get("mode") === "select";

    const [customers, setCustomers] = useState<any[]>([]);
    const [pageInfo, setPageInfo] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // Every customer seen on any page, so selections from other pages keep their name/mobile
    const seenCustomersRef = useRef<Map<number, any>>(new Map());

    const [deleteTarget, setDeleteTarget] = useState<{ id: string | number; name: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    const [filters, setFilters] = useState({ fullName: "", mobile: "" });
    const [page, setPage] = useState(0);
    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => {
        setPage(0);
    }, [debouncedFilters, pageSize]);

    const loadCustomers = async () => {
        setLoading(true);
        setError("");
        try {
            const result = await dispatch(
                fetchCustomers({ page, size: pageSize, filters: debouncedFilters })
            ).unwrap();
            setCustomers(result.results);
            (result.results || []).forEach((c: any) => seenCustomersRef.current.set(c.id, c));
            setPageInfo(result.page);
            localStorage.setItem("customers", JSON.stringify(result.results));
        } catch (err: any) {
            setError(err?.message || "Failed to load customers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, [dispatch, page, pageSize, debouncedFilters]);

    const handleEdit = (customerId: string | number) => {
        navigate(`/admin/customers/edit/${customerId}`);
    };

    const handleDeleteClick = (id: string | number, name: string) => {
        setDeleteTarget({ id, name });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await dispatch(deleteCustomer(deleteTarget.id)).unwrap();
            toast.success("Customer deleted successfully!");
            setDeleteTarget(null);
            loadCustomers();
        } catch (err: any) {
            toast.error(err?.message || "Failed to delete customer");
        } finally {
            setDeleting(false);
        }
    };

    const toggleSelect = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const allIds = customers.map((c) => c.id);
        const allSelected = allIds.every((id) => selectedIds.has(id));
        if (allSelected) {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                allIds.forEach((id) => next.delete(id));
                return next;
            });
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                allIds.forEach((id) => next.add(id));
                return next;
            });
        }
    };

    const handleConfirmSelection = () => {
        if (selectedIds.size === 0) {
            toast.error("Please select at least one customer");
            return;
        }

        const ids = Array.from(selectedIds);
        const selectedCustomers = ids
            .map((id) => seenCustomersRef.current.get(id))
            .filter(Boolean);

        const returnTo = location.state?.returnTo || "/send-message/single";
        navigate(returnTo, {
            state: {
                selectedCustomerIds: ids,
                selectedCustomerNames: selectedCustomers.map((c: any) => c.fullName),
                selectedCustomerMobiles: selectedCustomers.map((c: any) => c.mobile),
            },
        });
    };

    const allVisibleSelected =
        customers.length > 0 && customers.every((c) => selectedIds.has(c.id));

    const totalPages = pageInfo.count ?? 0;
    const currentStart = pageInfo.index != null ? pageInfo.index * pageInfo.size + 1 : 1;
    const currentEnd = Math.min((pageInfo.index + 1) * pageInfo.size, pageInfo.total || 0);

    return (
        <>
            <div className="bg-white rounded-3xl p-4 sm:p-5 mt-5 w-full relative">

                <ECSLoader show={loading} />

                {error && (
                    <div className="text-red-500 mb-3 text-sm font-medium">{error}</div>
                )}

                {/* Top Actions */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">

                    {/* Select mode: select-all checkbox on mobile */}
                    {isSelectMode && (
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-600 cursor-pointer sm:hidden">
                            <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                onChange={toggleSelectAll}
                            />
                            Select All
                        </label>
                    )}

                    <div className="flex items-center gap-3 ml-auto flex-wrap">
                        {isSelectMode && (
                            <button
                                onClick={handleConfirmSelection}
                                disabled={selectedIds.size === 0}
                                className="px-4 py-1.5 bg-blue-500 text-white text-sm font-semibold rounded-xl hover:bg-blue-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Confirm ({selectedIds.size})
                            </button>
                        )}

                        {/* Pagination */}
                        <Pagination
                            page={page}
                            setPage={setPage}
                            totalPages={totalPages}
                            totalItems={pageInfo.total || 0}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            loading={loading}
                        />
                    </div>
                </div>

                {/* Search filters (mobile-visible, stacked) */}
                <div className="flex flex-wrap gap-2 mb-3 sm:hidden">
                    <SearchBox
                        value={filters.fullName}
                        onChange={(v) => setFilters((p) => ({ ...p, fullName: v }))}
                        placeholder="Search name"
                    />
                    <SearchBox
                        value={filters.mobile}
                        onChange={(v) => setFilters((p) => ({ ...p, mobile: v }))}
                        placeholder="Search mobile"
                    />
                </div>

                {/* ── DESKTOP TABLE (hidden on mobile) ── */}
                <div
                    className={`hidden sm:block overflow-x-auto transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"}`}
                >
                    <table className="w-full table-fixed text-sm border-separate border-spacing-y-2">
                        <thead className="text-gray-600">
                            <tr>
                                <th className="p-2 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th className="p-2 text-left font-bold w-20">SL. No.</th>
                                <th className="p-2 text-left font-bold">Name</th>
                                <th className="p-2 text-left font-bold">Email</th>
                                <th className="p-2 text-left font-bold">Mobile Number</th>
                                <th className="p-2 text-left font-bold">Gender</th>
                                <th className="p-2 text-left font-bold">City</th>
                                <th className="p-2 w-24 text-left font-bold">Status</th>
                                {!isSelectMode && (
                                    <th className="p-2 w-24 text-left font-bold">Actions</th>
                                )}
                            </tr>
                            <tr>
                                <th />
                                <th />
                                <th>
                                    <SearchBox
                                        value={filters.fullName}
                                        onChange={(v) => setFilters((p) => ({ ...p, fullName: v }))}
                                    />
                                </th>
                                <th />
                                <th>
                                    <SearchBox
                                        value={filters.mobile}
                                        onChange={(v) => setFilters((p) => ({ ...p, mobile: v }))}
                                    />
                                </th>
                                <th /><th /><th />
                                {!isSelectMode && <th />}
                            </tr>
                        </thead>

                        <tbody>
                            {customers.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={isSelectMode ? 8 : 9} className="text-center py-8 text-gray-400">
                                        No customers found.
                                    </td>
                                </tr>
                            ) : (
                                customers.map((item, index) => {
                                    const isChecked = selectedIds.has(item.id);
                                    return (
                                        <tr
                                            key={item.id ?? index}
                                            onClick={isSelectMode ? () => toggleSelect(item.id) : undefined}
                                            className={`font-medium transition-colors ${isSelectMode ? "cursor-pointer" : ""} ${isChecked ? "bg-blue-50" : "even:bg-gray-50 odd:bg-white hover:bg-gray-100"}`}
                                        >
                                            <td className="p-2">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleSelect(item.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </td>
                                            <td className="p-2">{item.id}</td>
                                            <td className="p-2">{item.fullName}</td>
                                            <td className="p-2 truncate">{item.email}</td>
                                            <td className="p-2">{item.mobile}</td>
                                            <td className="p-2">{item.gender}</td>
                                            <td className="p-2">{item.city}</td>
                                            <td className="p-3">
                                                <StatusBadge active={item.status} />
                                            </td>
                                            {!isSelectMode && (
                                                <td className="p-3 flex gap-3">
                                                    <img
                                                        src={editIcon}
                                                        alt="Edit"
                                                        className="w-4 h-4 cursor-pointer"
                                                        onClick={() => handleEdit(item.id)}
                                                    />
                                                    <img
                                                        src={deleteIcon}
                                                        alt="Delete"
                                                        className="w-4 h-4 cursor-pointer"
                                                        onClick={() => handleDeleteClick(item.id, item.fullName)}
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── MOBILE CARD LIST (hidden on sm+) ── */}
                <div
                    className={`flex flex-col gap-3 sm:hidden transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"}`}
                >
                    {customers.length === 0 && !loading ? (
                        <div className="text-center py-8 text-gray-400 text-sm">No customers found.</div>
                    ) : (
                        customers.map((item, index) => {
                            const isChecked = selectedIds.has(item.id);
                            return (
                                <div
                                    key={item.id ?? index}
                                    onClick={isSelectMode ? () => toggleSelect(item.id) : undefined}
                                    className={`rounded-2xl border p-4 transition-colors ${isSelectMode ? "cursor-pointer" : ""} ${isChecked ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-100"}`}
                                >
                                    {/* Card header row */}
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleSelect(item.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="flex-shrink-0 mt-0.5"
                                            />
                                            <div className="min-w-0">
                                                <p className="font-bold text-gray-800 text-sm truncate">{item.fullName}</p>
                                                <p className="text-xs text-gray-400">ID: {item.id}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <StatusBadge active={item.status} />
                                            {!isSelectMode && (
                                                <div className="flex gap-2 ml-1">
                                                    <img
                                                        src={editIcon}
                                                        alt="Edit"
                                                        className="w-4 h-4 cursor-pointer"
                                                        onClick={(e) => { e.stopPropagation(); handleEdit(item.id); }}
                                                    />
                                                    <img
                                                        src={deleteIcon}
                                                        alt="Delete"
                                                        className="w-4 h-4 cursor-pointer"
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(item.id, item.fullName); }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Card detail rows */}
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs pl-7">
                                        <CardField label="Mobile" value={item.mobile} />
                                        <CardField label="Gender" value={item.gender} />
                                        <CardField label="City" value={item.city} />
                                        <CardField label="Email" value={item.email} truncate />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popup */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-web-background/40 backdrop-blur-sm"
                        onClick={() => !deleting && setDeleteTarget(null)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 flex flex-col gap-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto">
                            <img src={deleteIcon} alt="Delete" className="w-5 h-5" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-bold text-gray-800">Delete Customer</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Are you sure you want to delete{" "}
                                <span className="font-semibold text-gray-700">{deleteTarget.name}</span>
                                ? This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-2">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="flex-1 py-2 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition disabled:opacity-60"
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ── Small helpers ──────────────────────────────────────────────────────────────

function StatusBadge({ active }: { active: boolean }) {
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? "bg-green-500" : "bg-gray-400"}`} />
            {active ? "Active" : "Inactive"}
        </span>
    );
}

function CardField({ label, value, truncate }: { label: string; value?: string; truncate?: boolean }) {
    return (
        <div className="min-w-0">
            <span className="text-gray-400 font-medium">{label}: </span>
            <span className={`text-gray-700 font-semibold ${truncate ? "block truncate" : ""}`}>{value || "—"}</span>
        </div>
    );
}

function SearchBox({
    value = "",
    onChange,
    placeholder = "Search",
}: {
    value?: string;
    onChange?: (val: string) => void;
    placeholder?: string;
}) {
    return (
        <div className="relative w-full max-w-[160px]">
            <input
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                type="text"
                placeholder={placeholder}
                className="w-full border rounded-md h-8 pl-8 pr-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
            />
            <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
        </div>
    );
}